import React, { useContext, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import UserTileContext from '../../../contexts/userTilesContext';
import {
  ICheckItem,
  IConfirmModalProps,
  IModuleTile,
  IProgram,
  IProgramGroup,
  ISelectableModuleTile,
} from '../../../interfaces/interfaces';
import { availableModules, getModuleComponents } from '../../../modules';
import { loadProducts, loadTopics } from '../../../utils/data';
import getNextFreeSlot from '../../../utils/get-next-free-slot';
import keyboardTriggerBasic from '../../../utils/keyboard-trigger';
import List from '../List/List';
import ProgramList from '../List/ProgramList';
import Stepper from '../Stepper/Stepper';

function PersonalizationModal(props: IConfirmModalProps): JSX.Element {
  const { onConfirm, onAbort, isLocal } = props;
  const modalRef = useRef<HTMLAnchorElement>(null);
  const { t } = useTranslation();

  const [activeStep, setActiveStep] = useState(0);
  const [topics, setTopics] = useState<ICheckItem[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<ICheckItem[]>([]);
  const [programs, setPrograms] = useState<IProgram[]>([]);
  const [groups, setGroups] = useState<IProgramGroup[]>([]);
  const [programGroups, setProgramGroups] = useState<IProgramGroup[]>([]);
  const userTiles = useContext(UserTileContext);
  const [showAllPrograms, setShowAllPrograms] = useState(true);
  const [errorTopics, setErrorTopics] = useState<string>();
  const [errorProducts, setErrorProducts] = useState<string>();

  const apiErrorMessage = 'Beim Laden der Daten ist ein Fehler aufgetreten.';

  useEffect(() => {
    loadTopics(isLocal)
      .then((topic) => {
        setTopics(topic);
      })
      .catch(() => {
        setErrorTopics(apiErrorMessage);
      });

    loadProducts(isLocal)
      .then(({ products, productGroups }) => {
        setGroups(productGroups);

        const activeTileIds = userTiles.tiles.map((ut) => ut.tileRefId);
        const enrichedPrograms = products.map((p) => {
          // use only refTiles for ids in refTileIds
          const programRefTiles = p.tiles as IModuleTile[]; // ?.filter((pt) => p.tileRefId?.indexOf(pt.id || ''));
          return {
            ...p,
            tiles: programRefTiles?.map((tile) => {
              // tile exists on dashboard? pre-select it!
              if (activeTileIds.indexOf(tile.tileRefId) > -1) {
                return { ...tile, isSelected: true };
              }
              return { ...tile, id: tile.tileRefId };
            }),
          };
        });

        // assign groups
        productGroups.forEach((g) => {
          g.programs = (enrichedPrograms as IProgram[]).filter((p) => g.productIds.indexOf(p.id) > -1);
        });

        setProgramGroups(productGroups);
        setPrograms(enrichedPrograms as IProgram[]);
      })
      .catch(() => {
        setErrorProducts(apiErrorMessage);
      });
  }, []);

  const onAbortClick = () => {
    // close modal
    onAbort();
  };

  const onConfirmToppics = () => {
    setActiveStep(1);
  };

  const toggleProgramGrouping = () => {
    setShowAllPrograms(!showAllPrograms);
  };

  const onConfirmTileSelection = () => {
    let topicTilesToAddIds: string[] = [];
    let topicRefTiles: IModuleTile[] = [];

    // topic-related tiles
    selectedTopics.forEach((selTopic) => {
      if (selTopic.tileRefId?.length) {
        topicTilesToAddIds = [...topicTilesToAddIds, ...selTopic.tileRefId];
        topicRefTiles = [...topicRefTiles, ...(selTopic.tiles as IModuleTile[])];
      }
    });

    // collect refTiles for topicTilesToAddIds
    const refModules = getModuleComponents(availableModules);
    let newTopicTilesToAdd = topicRefTiles.map((rt) => {
      const refModule = refModules[rt.module];

      const w = rt.layout?.w || refModule.defaultLayout?.w || 1;
      const h = rt.layout?.h || refModule.defaultLayout?.h || 1;
      const tileId = rt.tileRefId;

      return {
        ...rt,
        id: tileId,
        layout: {
          i: tileId,
          x: 0, // will be set later
          y: 0, // will be set later
          w,
          h,
        },
      };
    });

    // remove already existing tiles
    newTopicTilesToAdd = (newTopicTilesToAdd as IModuleTile[]).filter(
      (tta) => (userTiles.tiles as IModuleTile[]).filter((tile) => tile.tileRefId === tta.tileRefId).length === 0,
    );

    // ------

    // add selected program tiles
    let newTilesFromProducts: IModuleTile[] = [];
    let withoutRemovedTiles: IModuleTile[] = userTiles.tiles as IModuleTile[];

    // collect
    programs.forEach((p) => {
      (p.tiles || []).forEach((tile) => {
        if (tile.isSelected) {
          const refModule = refModules[tile.module];
          const w = tile.layout?.w || refModule.defaultLayout?.w || 1;
          const h = tile.layout?.h || refModule.defaultLayout?.h || 1;
          newTilesFromProducts.push({
            ...tile,
            layout: {
              i: tile.tileRefId,
              x: 0, // will be set later
              y: 0, // will be set later
              w,
              h,
            },
          });
          return;
        }
        withoutRemovedTiles = withoutRemovedTiles.filter((existingTile) => tile.tileRefId !== existingTile.tileRefId);
      });
    });

    // remove removed and readded tiles from newTiles to prevent duplication
    newTilesFromProducts = newTilesFromProducts.filter(
      (nt) => withoutRemovedTiles.map((ot) => ot.tileRefId).indexOf(nt.tileRefId) === -1,
    );

    // remove topic-added tiles from newTiles
    newTopicTilesToAdd = newTopicTilesToAdd.filter(
      (ntt) => newTilesFromProducts.map((nt) => nt.id).indexOf(ntt.tileRefId || '') === -1,
    );

    // all tiles to add are set: Calculate positions of new tiles in grid based on already existing tiles' position
    const newTiles: IModuleTile[] = [];
    [...newTilesFromProducts, ...newTopicTilesToAdd].forEach((tile) => {
      const currentTiles = [...withoutRemovedTiles, ...newTiles];
      const nextFreeSlot = getNextFreeSlot(currentTiles as IModuleTile[], tile.layout.w);

      tile.layout = { ...tile.layout, ...nextFreeSlot };
      newTiles.push(tile as IModuleTile);
    });

    // remove AdvantageCommunicationModule tile
    withoutRemovedTiles = withoutRemovedTiles.filter((ti) => {
      return ti.module !== 'AdvantageCommunicationModule';
    });

    // -----
    const updatedSet = [...withoutRemovedTiles, ...newTiles];
    // update tileset
    userTiles.setTiles(updatedSet as IModuleTile[]);
    // close modal
    onConfirm();
  };

  const checkTopic = (items: ICheckItem[], item: ICheckItem | null, isChecked: boolean) => {
    return [
      ...items.map((topic) => {
        const tTopic = { ...topic };
        if (item === null || tTopic.id === item.id) {
          tTopic.checked = isChecked;
        }
        if (tTopic.topics) {
          tTopic.topics = checkTopic(tTopic.topics, item, isChecked);
        }
        return tTopic;
      }),
    ];
  };

  const checkProgram = (item: ISelectableModuleTile, isChecked: boolean) => {
    return [
      ...programs.map((program) => {
        const tProgram = { ...program };
        tProgram.tiles = tProgram.tiles?.map((tile) => {
          const tTile = { ...tile };
          if (tTile.tileRefId === item.tileRefId) {
            tTile.isSelected = isChecked;
          }
          return tTile;
        });

        return tProgram;
      }),
    ];
  };

  const onItemChange = (item: ICheckItem, isChecked: boolean) => {
    setTopics(checkTopic(topics, item, isChecked));
    setSelectedTopics(
      isChecked ? [...selectedTopics, item] : [...selectedTopics.filter((topic) => topic.id !== item.id)],
    );
  };

  const onProgramChange = (item: ISelectableModuleTile, isChecked: boolean) => {
    const updatedPrograms = checkProgram(item, isChecked);
    groups.forEach((g) => {
      g.programs = updatedPrograms.filter((p) => g.productIds.indexOf(p.id) > -1);
    });
    setProgramGroups(groups);
    setPrograms(updatedPrograms);
  };

  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  const step1 = (
    <>
      <div className="scrollable-content">
        <h3 className="hl-3">
          <span className="hl-no">1 </span>
          <Trans i18nKey="cockpit.personalization.headlineTopics">Headline Themen</Trans>
        </h3>
        <p className="tx_4" dangerouslySetInnerHTML={{ __html: t('cockpit.personalization.introTopics') }} />

        {errorTopics && <div className="api-error">{errorTopics}</div>}
        {topics.length === 0 && !errorTopics && 'Daten werden geladen...'}
        <List items={topics} isChecklist onItemChange={onItemChange} />
      </div>

      <div className="modal-actions">
        <a
          role="button"
          className="link--button link--button-frameless"
          onClick={onAbortClick}
          onKeyDown={(e) => {
            keyboardTriggerBasic(e, onAbortClick);
          }}
          tabIndex={0}
        >
          <span className="ic-timeout" />
          <span className="button-name">
            <Trans i18nKey="cockpit.personalization.abortButton">Später festlegen</Trans>
          </span>
        </a>
        <a
          role="button"
          className="link--button"
          onClick={onConfirmToppics}
          onKeyDown={(e) => {
            keyboardTriggerBasic(e, onConfirmToppics);
          }}
          tabIndex={0}
          ref={modalRef}
        >
          <span className="ic-arrowRight" />
          <span className="button-name">
            <Trans i18nKey="cockpit.personalization.confirmTopicsButton">Themen auswählen</Trans>
          </span>
        </a>
      </div>
    </>
  );

  const step2 = (
    <>
      <div className="scrollable-content step2">
        <h3 className="hl-3">
          <span className="hl-no">2 </span>
          <Trans i18nKey="cockpit.personalization.headlineProducts">Headline Programme</Trans>
        </h3>
        <p className="tx_4" dangerouslySetInnerHTML={{ __html: t('cockpit.personalization.introProducts') }} />

        {errorProducts && <div className="api-error">{errorProducts}</div>}

        {programs.length === 0 && !errorProducts && 'Programme werden geladen...'}
        <ProgramList
          programGroups={programGroups}
          items={programs}
          onItemChange={onProgramChange}
          useGrouping={showAllPrograms}
        />
      </div>

      <div className="modal-actions">
        <a
          role="button"
          className="link--button link--button-frameless"
          onClick={onAbortClick}
          onKeyDown={(e) => {
            keyboardTriggerBasic(e, onAbortClick);
          }}
          tabIndex={0}
        >
          <span className="ic-timeout" />
          <span className="button-name">
            <Trans i18nKey="cockpit.personalization.abortButton">Später festlegen</Trans>
          </span>
        </a>
        <a
          role="button"
          className="link--button link--button-secondary"
          onClick={toggleProgramGrouping}
          onKeyDown={(e) => {
            keyboardTriggerBasic(e, toggleProgramGrouping);
          }}
          tabIndex={0}
        >
          <span className={showAllPrograms ? 'ic-arrowUp' : 'ic-arrowDown'} />
          <span className="button-name">
            {
              showAllPrograms ?
              <Trans i18nKey="cockpit.personalization.showAllButton">Alle Produkte anzeigen</Trans> :
              <Trans i18nKey="cockpit.personalization.reduceAllButton">Produktliste reduzieren</Trans>
            }
          </span>
        </a>
        <a
          role="button"
          className="link--button"
          onClick={onConfirmTileSelection}
          onKeyDown={(e) => {
            keyboardTriggerBasic(e, onConfirmTileSelection);
          }}
          tabIndex={0}
          ref={modalRef}
        >
          <span className="ic-arrowRight" />
          <span className="button-name">
            <Trans i18nKey="cockpit.personalization.confirmProductsButton">Module auswählen</Trans>
          </span>
        </a>
      </div>
    </>
  );

  return (
    <>
      <div className="modal personalization-modal" role="dialog">
        <div className="modal-inner l-container">
          <a
            role="button"
            className="modal-closer"
            onClick={onAbortClick}
            onKeyDown={(e) => {
              keyboardTriggerBasic(e, onAbortClick);
            }}
            tabIndex={0}
          >
            <span className="ic-close" />
          </a>

          <div className="modal-inner-content">
            <Stepper
              steps={[t('cockpit.personalization.step1'), t('cockpit.personalization.step2')]}
              activeIndex={activeStep}
              setActiveStep={setActiveStep}
            />
            {activeStep === 0 && step1}
            {activeStep === 1 && step2}
          </div>
        </div>
      </div>
    </>
  );
}
export default PersonalizationModal;
